import { createApi, fakeBaseQuery } from '@reduxjs/toolkit/query/react';

import { resourcesApi } from '../resources/resourcesApi';

import type { Permission, PermissionLevel, ObjectType } from '@/shared/types/permission';
import type { SharedCurriculumItem } from '@/shared/types/sharedCurriculum';
import { sbq, throwOnError } from '@/shared/utils/supabaseQuery';
import { coursesApi } from '@/features/my-courses/coursesApi';
import { unitsApi } from '@/features/units/unitsApi';
import { lessonsApi } from '@/features/lessons/lessonsApi';

export const permissionsApi = createApi({
  reducerPath: 'permissionsApi',
  baseQuery: fakeBaseQuery(),
  tagTypes: ['Permission'],
  endpoints: (builder) => ({
    getPermissionsForObject: builder.query<
      Permission[],
      { object_type: ObjectType; object_id: string }
    >({
      queryFn: sbq(async (sb, { object_type, object_id }) => {
        const rows = throwOnError(
          await sb.rpc('list_object_permissions', {
            p_object_type: object_type,
            p_object_id: object_id,
          })
        );
        return rows as Permission[];
      }, 'Permissions'),
      providesTags: ['Permission'],
    }),

    upsertPermission: builder.mutation<
      Permission,
      {
        user_id?: string | null;
        team_id?: string | null;
        object_type: ObjectType;
        object_id: string;
        permission_type: PermissionLevel;
      }
    >({
      queryFn: sbq(async (sb, payload) => {
        const result = throwOnError(
          await sb.rpc('grant_permission', {
            p_user_id: payload.user_id ?? null,
            p_team_id: payload.team_id ?? null,
            p_object_type: payload.object_type,
            p_object_id: payload.object_id,
            p_permission: payload.permission_type,
          })
        );
        return result as Permission;
      }, 'Permission'),
      invalidatesTags: ['Permission'],
    }),

    deletePermission: builder.mutation<void, string>({
      queryFn: sbq(async (sb, id) => {
        throwOnError(await sb.rpc('revoke_permission', { p_permission_id: id }));
        return undefined;
      }, 'Permission'),
      invalidatesTags: ['Permission'],
    }),

    checkObjectPermission: builder.query<
      boolean,
      { object_type: ObjectType; object_id: string; min?: PermissionLevel }
    >({
      queryFn: sbq(async (sb, { object_type, object_id, min }) => {
        const result = throwOnError(
          await sb.rpc('check_object_permission', {
            p_object_type: object_type,
            p_object_id: object_id,
            p_min: min ?? 'editor',
          })
        );
        return result as boolean;
      }, 'Permission'),
      providesTags: ['Permission'],
    }),

    checkObjectExists: builder.query<boolean, { object_type: ObjectType; object_id: string }>({
      queryFn: sbq(async (sb, { object_type, object_id }) => {
        const result = throwOnError(
          await sb.rpc('check_object_exists', {
            p_object_type: object_type,
            p_object_id: object_id,
          })
        );
        return result as boolean;
      }, 'Object exists'),
    }),

    getSharedWithMe: builder.query<SharedCurriculumItem[], void>({
      queryFn: sbq(async (sb) => {
        const rows = throwOnError(await sb.rpc('get_shared_with_me'));
        return (rows ?? []) as SharedCurriculumItem[];
      }, 'Shared with me'),
      providesTags: ['Permission'],
    }),

    setPublicCascade: builder.mutation<
      void,
      { object_type: ObjectType; object_id: string; is_public: boolean }
    >({
      queryFn: sbq(async (sb, { object_type, object_id, is_public }) => {
        const { error } = await sb.rpc('set_public_cascade', {
          p_object_type: object_type,
          p_object_id: object_id,
          p_is_public: is_public,
        });
        if (error) throw error;
        return undefined as unknown as void;
      }, 'Permission'),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        dispatch(coursesApi.util.invalidateTags(['Course']));
        dispatch(unitsApi.util.invalidateTags(['Unit']));
        dispatch(lessonsApi.util.invalidateTags(['Lesson']));
        dispatch(resourcesApi.util.invalidateTags(['Resource', 'ResourceList']));
      },
    }),
  }),
});

export const {
  useGetPermissionsForObjectQuery,
  useUpsertPermissionMutation,
  useDeletePermissionMutation,
  useCheckObjectPermissionQuery,
  useCheckObjectExistsQuery,
  useGetSharedWithMeQuery,
  useSetPublicCascadeMutation,
} = permissionsApi;
